<script setup lang="ts">
import { Styles } from '@ui/styles';
import { usePriceButton } from '../calendar/usePriceButton';
import type { IOrderStore } from '@sub/stores/order';
import { durationDifference, formatHM, formatTime, teleportSlotToTz } from '@sub/utils/times';
import type { SlotAvailability } from '@utils/calendar/Disabled';

const props = withDefaults(defineProps<{
  order?: IOrderStore;
  slotAvailability?: SlotAvailability;
}>(), {
  order: () => ({}),
});

const { order, slotAvailability } = toRefs(props);

const { minLengthError, price, isWithout, text, minTimeLengthText, seats } = usePriceButton(order, slotAvailability);
const i18n = useI18n();

const time = computed(() => {
  const dates = order.value.service?.addresses?.[0].isOnline && order.value.dates ? teleportSlotToTz(order.value.dates, order.value.service.addresses[0].timezone) : order.value.dates;
  return {
    dayText: dates ? formatDate(dates.from, 'eee, dd, MMMM') : '',
    year: dates ? formatDate(dates.from, ', yyyy') : '',
    time: dates ? `${formatTime(dates.from)} — ${formatTime(dates.to)}` : '',
    duration: dates ? formatHM(durationDifference(order.value.dates.to, order.value.dates.from), i18n) : '',
  };
});
</script>

<template lang="pug">
.service-order.br-16.flex(:class="{complete: order.dates}")
  .flex.col-4.overflow-hidden
    .typ-14-18-400 {{ order.service?.title }}
    .order-info.flex-wrap-wrap(v-if="order.target || order.dates")
      .info(v-if="order.target") {{ order.targetData.name }}
      template(v-if="order.dates")
        span.info.day {{ time.dayText }}
          span.year {{ time.year }}
        span.info.time {{ time.time }}
        span.info.duration {{ time.duration }}
    template(v-if="order.dates")
      .spots.typ-11-14-400.m-t-4(v-if="seats") {{ $t('page.booking.service.info.available-spots', {value: seats.max})}}
  template(v-if="order.dates")
    UiButton.book(v-if="minLengthError" :label="$t('page.booking.service.info.button.min-length.text', {value: minTimeLengthText})" :disabled="true"  :size="Styles.Sizes.Small")
    UiButton.book.white-space-break-spaces(v-else-if="price?.general || (isWithout && order.dates.to)" :label="text" :size="Styles.Sizes.Small" :to="`/booking/${order.service.id}/order`")
</template>

<style scoped lang="scss">
.service-order {
  box-shadow: rgba(0, 0, 0, 0.2) 0px 1px 20px;
  background: #FFFFFF;
  color: #1F1F1F;
  --uno: w-100p row-12 p-6 p-l-12 flex-between-center;
  transition: .3s ease-in-out;
  transition-property: transform, opacity;

  opacity: 0;
  transform: translateY(100px);

  &.complete {
    opacity: 1;
    transform: unset;
  }

  @screen md {
    --uno: w-unset w-255 col-12 flex-unset-unset p-16;
    opacity: 1;
    transform: unset;
    box-shadow: 0px 1px 0px 0px #E9E9E980 inset;
    background: #EEF7FF;
  }
}

.book {
  --uno: br-18 typ-14-18-400;
  text-align: right;
  text-wrap: nowrap;

  @screen md {
    --uno: typ-14-20-400;
    text-align: center;
  }
}

.order-info {
  --uno: flex;

  @screen md {
    display: contents;
  }
}

.info {
  --uno: typ-11-14-400;
  &:before {
    content: "•";
    --uno: p-0-4;
    color: #ABB1B6;
  }

  &:first-child {
    &:before {
      content: none;
    }
  }

  @screen md {
    --uno: typ-14-18-300;
    &:before {
      content: none;
    }
  }
}

.day {
  @screen md {
    --uno: m-t-12;
  }
}

.year {
  --uno: display-none;
  @screen sm {
    --uno: display-unset;
  }
}

.time {
  display: none;

  @screen md {
    display: unset;
  }
}

.duration {
  color: #5B646D;

  @screen md {
    --uno: typ-13-18-300;
  }
}

.spots {
  color: #5B646D;
}
</style>